import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { AlertTriangle, ArrowLeft, ChevronLeft, ChevronRight, Clock, Download, MessageCircle, MessagesSquare, MousePointerClick, RefreshCw, Search, TrendingUp, UserRound, Users, XCircle } from "lucide-react"
import { Link } from "react-router-dom"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { StatTile } from "@/components/shared/stat-tile"
import { StatusBadge } from "@/components/shared/status-badge"
import { api } from "@/lib/api"
import { queryKeys } from "@/lib/query-keys"
import type { AnalyticsSummary, ConversationDetail, ConversationListItem } from "@/lib/types"

type RangeKey = "today" | "7d" | "30d" | "custom"

function rangeDates(key: RangeKey, customFrom: string, customTo: string): { from?: string; to?: string } {
  const now = new Date()
  if (key === "today") {
    const start = new Date(now)
    start.setHours(0, 0, 0, 0)
    return { from: start.toISOString(), to: now.toISOString() }
  }
  if (key === "7d") return { to: now.toISOString(), from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString() }
  if (key === "30d") return { to: now.toISOString(), from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString() }
  return {
    from: customFrom ? new Date(`${customFrom}T00:00:00`).toISOString() : undefined,
    to: customTo ? new Date(`${customTo}T23:59:59`).toISOString() : undefined,
  }
}

function formatDateTime(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date)
}

function formatDay(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.slice(5).replace("-", "/")
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" }).format(date)
}

function formatDuration(ms: number | null) {
  if (ms === null) return "—"
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}min ${seconds % 60}s`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}min`
}

function shortId(id: string) {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}

function statusTone(status: ConversationListItem["status"]): "success" | "warning" | "danger" | "neutral" {
  if (status === "active") return "success"
  if (status === "abandoned") return "danger"
  if (status === "closed") return "neutral"
  return "warning"
}

function statusLabel(status: ConversationListItem["status"]) {
  if (status === "active") return "Ativa"
  if (status === "closed") return "Encerrada"
  if (status === "abandoned") return "Abandonada"
  return "Sem mensagens"
}

function RangePicker({ range, customFrom, customTo, onRangeChange, onCustomFromChange, onCustomToChange }: {
  range: RangeKey
  customFrom: string
  customTo: string
  onRangeChange: (range: RangeKey) => void
  onCustomFromChange: (value: string) => void
  onCustomToChange: (value: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={range} onValueChange={(next) => onRangeChange(next as RangeKey)}>
        <SelectTrigger className="h-9 w-[150px] rounded-xl text-xs" aria-label="Período"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="today">Hoje</SelectItem>
          <SelectItem value="7d">Últimos 7 dias</SelectItem>
          <SelectItem value="30d">Últimos 30 dias</SelectItem>
          <SelectItem value="custom">Personalizado</SelectItem>
        </SelectContent>
      </Select>
      {range === "custom" && (
        <>
          <Input type="date" aria-label="De" value={customFrom} onChange={(event) => onCustomFromChange(event.target.value)} className="h-9 w-[150px] rounded-xl text-xs" />
          <Input type="date" aria-label="Até" value={customTo} onChange={(event) => onCustomToChange(event.target.value)} className="h-9 w-[150px] rounded-xl text-xs" />
        </>
      )}
    </div>
  )
}

type DailyKey = "count" | "whatsappClicks" | "genesysHandoffs"
type DailySeries = { key: DailyKey; label: string; color: string; width: number }

export function DailyLineChart({ data }: { data: AnalyticsSummary["perDay"] }) {
  const hasWhatsapp = data.some((day) => typeof day.whatsappClicks === "number")
  const hasGenesys = data.some((day) => typeof day.genesysHandoffs === "number")
  const series: DailySeries[] = [
    { key: "count", label: "Conversas", color: "var(--primary)", width: 2.5 },
    ...(hasWhatsapp ? [{ key: "whatsappClicks" as const, label: "Cliques no WhatsApp", color: "var(--success)", width: 2.5 }] : []),
    ...(hasGenesys ? [{ key: "genesysHandoffs" as const, label: "Transbordos Genesys confirmados", color: "#d946ef", width: 4 }] : []),
  ]
  const values = (key: DailyKey) => data.map((day) => {
    const value = day[key]
    return typeof value === "number" && Number.isFinite(value) ? Math.max(0, value) : 0
  })
  const max = Math.max(1, ...series.flatMap(({ key }) => values(key)))
  const width = 740
  const height = 236
  const left = 48
  const right = 12
  const top = 14
  const bottom = 194
  const xAt = (index: number) => data.length <= 1 ? (left + width - right) / 2 : left + (index / (data.length - 1)) * (width - left - right)
  const yAt = (value: number) => bottom - (value / max) * (bottom - top)
  const tickIndexes = new Set<number>([0, Math.max(0, data.length - 1)])
  const tickEvery = Math.max(1, Math.ceil((data.length - 1) / 5))
  for (let index = 0; index < data.length; index += tickEvery) tickIndexes.add(index)

  if (!data.length) return <p className="muted py-8 text-center text-xs">Sem registros diários neste período.</p>

  return (
    <div>
      <svg className="h-[220px] w-full" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={series.map(({ label }) => label).join(", ") + " por dia"}>
        {[0, 0.5, 1].map((step) => {
          const value = max * step
          const y = yAt(value)
          return <g key={step} className="text-muted-foreground"><line x1={left} x2={width - right} y1={y} y2={y} stroke="currentColor" strokeOpacity="0.16" /><text x={left - 8} y={y + 3} textAnchor="end" fill="currentColor" fontSize="9">{Math.round(value).toLocaleString("pt-BR")}</text></g>
        })}
        {series.map((item) => {
          const points = values(item.key)
          const coordinates = points.map((value, index) => `${xAt(index)},${yAt(value)}`).join(" ")
          return (
            <g key={item.key}>
              <polyline points={coordinates} fill="none" stroke={item.color} strokeWidth={item.width} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              {points.map((value, index) => (
                <circle key={data[index].day} cx={xAt(index)} cy={yAt(value)} r={item.key === "genesysHandoffs" ? 3.5 : 2.5} fill="var(--card)" stroke={item.color} strokeWidth={item.key === "genesysHandoffs" ? 2.5 : 1.75} vectorEffect="non-scaling-stroke" tabIndex={0} role="img" aria-label={`${formatDay(data[index].day)} · ${item.label}: ${value.toLocaleString("pt-BR")}`} className="outline-none focus-visible:stroke-[3]">
                  <title>{formatDay(data[index].day)} · {item.label}: {value.toLocaleString("pt-BR")}</title>
                </circle>
              ))}
            </g>
          )
        })}
        {[...tickIndexes].sort((a, b) => a - b).map((index) => (
          <text key={data[index].day} x={xAt(index)} y={height - 8} textAnchor={index === 0 ? "start" : index === data.length - 1 ? "end" : "middle"} fill="currentColor" className="text-muted-foreground" fontSize="9">{formatDay(data[index].day)}</text>
        ))}
      </svg>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-[11px]">
        {series.map((item) => (
          <li key={item.key} className={item.key === "genesysHandoffs" ? "flex items-center gap-2 rounded-full bg-fuchsia-500/10 px-2.5 py-1 font-semibold text-fuchsia-700 dark:text-fuchsia-300" : "flex items-center gap-2 text-muted-foreground"}>
            <span aria-hidden="true" className="h-0.5 w-4 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
          </li>
        ))}
      </ul>
      {(!hasWhatsapp || !hasGenesys) && (
        <p className="muted mt-3 text-[10px]">As séries diárias de cliques e transbordos confirmados aparecem quando esses eventos forem enviados pela API.</p>
      )}
    </div>
  )
}

function Funnel({ summary }: { summary: AnalyticsSummary }) {
  const rows = [
    { label: "Aberturas do balão", value: summary.totals.balloonSessions, icon: MousePointerClick, rate: null as string | null },
    { label: "Conversas iniciadas", value: summary.totals.conversations, icon: MessagesSquare, rate: summary.totals.balloonSessions ? `${((summary.totals.conversations / Math.max(1, summary.totals.balloonSessions)) * 100).toFixed(0)}% do balão` : null },
    { label: "Mensagens trocadas", value: summary.totals.messages, icon: MessageCircle, rate: summary.totals.conversations ? `${summary.totals.avgMessagesPerConversation} por conversa` : null },
    { label: "Cliques no WhatsApp", value: summary.totals.whatsappClicks, icon: TrendingUp, rate: summary.totals.conversations ? `${((summary.totals.whatsappClicks / Math.max(1, summary.totals.conversations)) * 100).toFixed(0)}% das conversas` : null },
  ]
  const max = Math.max(1, ...rows.map(({ value }) => value))
  return (
    <div className="space-y-3">
      {rows.map(({ label, value, icon: Icon, rate }) => (
        <div key={label}>
          <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
            <span className="flex items-center gap-1.5 font-medium"><Icon className="size-3.5 text-muted-foreground" />{label}</span>
            <span className="flex items-baseline gap-2"><span className="font-bold">{value}</span>{rate && <span className="muted text-[10px]">{rate}</span>}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="bg-primary h-full rounded-full transition-all" style={{ width: `${Math.max(2, (value / max) * 100)}%` }} /></div>
        </div>
      ))}
      {summary.prepared?.balloonFunnel && (
        <p className="muted text-[11px]">Funil do balão em coleta: o rastreamento de abertura começou agora e os números crescem com os novos registros.</p>
      )}
    </div>
  )
}

export function AnalyticsPage({ chatbotId, chatbotName }: { chatbotId: string; chatbotName: string }) {
  const [range, setRange] = useState<RangeKey>("30d")
  const [customFrom, setCustomFrom] = useState("")
  const [customTo, setCustomTo] = useState("")
  const { from, to } = useMemo(() => rangeDates(range, customFrom, customTo), [range, customFrom, customTo])
  const summaryQuery = useQuery({
    queryKey: queryKeys.analytics(chatbotId, from, to),
    queryFn: () => api.analyticsSummary(chatbotId, from, to),
  })
  const summary = summaryQuery.data

  return (
    <div className="space-y-5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="font-display text-[19px] font-bold tracking-[-.02em]">Insights · {chatbotName}</h2>
          <p className="muted mt-1 text-[12px]">Dados observados de uso; categorias de temas estimadas por palavras-chave.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-xl"
            aria-label="Atualizar insights"
            title="Atualizar insights"
            aria-busy={summaryQuery.isFetching}
            disabled={summaryQuery.isFetching}
            onClick={() => void summaryQuery.refetch()}
          >
            <RefreshCw className={summaryQuery.isFetching ? "size-3.5 animate-spin" : "size-3.5"} />
          </Button>
          <RangePicker
            range={range}
            customFrom={customFrom}
            customTo={customTo}
            onRangeChange={setRange}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />
        </div>
      </div>

      {summaryQuery.isPending ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" aria-busy="true">{[1, 2, 3, 4, 5].map((index) => <Skeleton key={index} className="h-[92px] rounded-2xl" />)}</div>
      ) : summaryQuery.isError ? (
        <Alert variant="destructive"><AlertTriangle className="size-4" /><AlertTitle>Não foi possível carregar os insights</AlertTitle><AlertDescription className="flex items-center justify-between gap-3"><span>{summaryQuery.error instanceof Error ? summaryQuery.error.message : "Tente novamente."}</span><Button variant="outline" size="sm" onClick={() => void summaryQuery.refetch()}><RefreshCw className="size-3.5" />Tentar novamente</Button></AlertDescription></Alert>
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" aria-label="Indicadores principais">
            <StatTile label="Conversas" value={summary.totals.conversations} hint={summary.totals.conversationsWithMessages + " com mensagens"} />
            <StatTile label="Mensagens" value={summary.totals.messages} hint={summary.totals.avgMessagesPerConversation + " por conversa"} />
            <StatTile label="Cliques no WhatsApp" value={summary.totals.whatsappClicks} tone={summary.totals.whatsappClicks ? "success" : undefined} hint={summary.totals.conversations ? `${((summary.totals.whatsappClicks / Math.max(1, summary.totals.conversations)) * 100).toFixed(0)}% das conversas` : "Sem conversas"} />
            <StatTile label="Visitantes únicos" value={summary.totals.uniqueVisitors} hint="Sessões distintas" />
            <StatTile label="Transbordos Genesys" value={summary.totals.genesysHandoffs} hint="Confirmados no período" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <section className="panel-bg rounded-[22px] border border-border p-5 lg:col-span-2" aria-labelledby="daily-chart-title">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 id="daily-chart-title" className="font-display text-[15px] font-bold">Atividade diária</h3>
                  <p className="muted mt-0.5 text-[11px]">Cliques no link do WhatsApp não confirmam transferência; transbordos Genesys são handoffs confirmados. Foque os pontos para ouvir os valores por dia.</p>
                </div>
                {summary.perDay.some((day) => typeof day.genesysHandoffs === "number") && <span className="rounded-full bg-fuchsia-500/10 px-2.5 py-1 text-[10px] font-semibold text-fuchsia-700 dark:text-fuchsia-300">Genesys · confirmados</span>}
              </div>
              <div className="mt-3"><DailyLineChart data={summary.perDay} /></div>
            </section>
            <section className="panel-bg rounded-[22px] border border-border p-5">
              <h3 className="font-display text-[15px] font-bold">Balão → conversa → mensagens</h3>
              <div className="mt-4"><Funnel summary={summary} /></div>
            </section>
            <section className="panel-bg rounded-[22px] border border-border p-5">
              <h3 className="font-display text-[15px] font-bold">Categorias e temas estimados</h3>
              <p className="muted mt-0.5 text-[11px]">Estimativas por palavras-chave em todo o histórico do assistente, independentes do período do gráfico.</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {summary.topics.length ? summary.topics.map(({ term, count }) => (
                  <span key={term} className="rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] font-semibold">{term} <span className="muted font-normal">· {count}</span></span>
                )) : <p className="muted text-[11px]">Nenhum tema identificado no histórico de conversas.</p>}
              </div>
              <p className="muted mt-2 text-[10px] leading-relaxed">
                Histórico completo: {summary.topicCoverage.visitorMessagesAnalyzedAllTime} mensagens de visitantes em {summary.topicCoverage.conversationsWithVisitorMessagesAllTime} de {summary.topicCoverage.conversationsAllTime} conversas; {summary.topicCoverage.conversationsClassifiedAllTime} conversas receberam uma categoria. Estimativa por palavras-chave, sem IA.
              </p>
              <h3 className="font-display mt-5 text-[15px] font-bold">Links mais clicados</h3>
              <div className="mt-2 space-y-1.5">
                {summary.topLinks.length ? summary.topLinks.map(({ host, count }) => (
                  <div key={host} className="flex items-center justify-between text-xs"><span className="truncate font-medium">{host}</span><span className="muted">{count} cliques</span></div>
                )) : <p className="muted text-[11px]">Nenhum clique registrado no período.</p>}
              </div>
            </section>
            <section className="panel-bg rounded-[22px] border border-border p-5 lg:col-span-2" aria-label="Tokens consumidos">
              <h3 className="font-display text-[15px] font-bold">Tokens consumidos</h3>
              <p className="muted mt-0.5 text-[11px]">Somatório do uso da OpenAI no período, por assistente.</p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center sm:max-w-xl">
                <div className="rounded-xl border border-border bg-muted/20 px-2 py-2.5"><p className="text-xs text-muted-foreground">Total</p><p className="font-display mt-0.5 text-lg font-extrabold">{(summary.totals.totalTokens ?? 0).toLocaleString("pt-BR")}</p></div>
                <div className="rounded-xl border border-border bg-muted/20 px-2 py-2.5"><p className="text-xs text-muted-foreground">Entrada</p><p className="font-display mt-0.5 text-lg font-extrabold">{(summary.totals.promptTokens ?? 0).toLocaleString("pt-BR")}</p></div>
                <div className="rounded-xl border border-border bg-muted/20 px-2 py-2.5"><p className="text-xs text-muted-foreground">Saída</p><p className="font-display mt-0.5 text-lg font-extrabold">{(summary.totals.completionTokens ?? 0).toLocaleString("pt-BR")}</p></div>
              </div>
            </section>
          </div>

          <div className="panel-bg flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-[22px] border border-border px-5 py-3 text-[11px] muted" aria-label="Saúde das conversas">
            <span className="flex items-center gap-1.5"><Users className="size-3.5" />{summary.totals.conversationsWithMessages} com mensagens</span>
            <span className="flex items-center gap-1.5"><MousePointerClick className="size-3.5" />{summary.totals.balloonSessions} aberturas · {summary.totals.conversionBalloonToConversation === null ? "—" : `${(summary.totals.conversionBalloonToConversation * 100).toFixed(1)}% convertem`}</span>
            <span className="flex items-center gap-1.5"><Clock className="size-3.5" />Tempo médio {formatDuration(summary.totals.avgDurationMs)}</span>
            <span className="flex items-center gap-1.5"><XCircle className="size-3.5" />{summary.totals.abandoned} abandonadas · {summary.totals.closed} encerradas</span>
            {summary.totals.errors > 0 && <span className="flex items-center gap-1.5 text-destructive"><AlertTriangle className="size-3.5" />{summary.totals.errors} com erro</span>}
          </div>
        </>
      ) : null}
    </div>
  )
}

export function ConversationHistoryPage({ chatbotId, chatbotName }: { chatbotId: string; chatbotName: string }) {
  const [range, setRange] = useState<RangeKey>("30d")
  const [customFrom, setCustomFrom] = useState("")
  const [customTo, setCustomTo] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("all")
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { from, to } = useMemo(() => rangeDates(range, customFrom, customTo), [range, customFrom, customTo])
  const listParams = useMemo(() => ({ from, to, q: search || undefined, status, page, pageSize: 20 }), [from, to, search, status, page])
  const listKey = useMemo(() => JSON.stringify(listParams), [listParams])
  const listQuery = useQuery({
    queryKey: queryKeys.conversations(chatbotId, listKey),
    queryFn: () => api.listConversations(chatbotId, listParams),
  })
  const totalPages = listQuery.data ? Math.max(1, Math.ceil(listQuery.data.total / listQuery.data.pageSize)) : 1

  return (
    <div className="space-y-5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="font-display text-[19px] font-bold tracking-[-.02em]">Histórico · {chatbotName}</h2>
          <p className="muted mt-1 text-[12px]">Conversas registradas; a identificação da sessão é anônima.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-xl" aria-label="Atualizar histórico" title="Atualizar histórico" aria-busy={listQuery.isFetching} disabled={listQuery.isFetching} onClick={() => void listQuery.refetch()}>
            <RefreshCw className={listQuery.isFetching ? "size-3.5 animate-spin" : "size-3.5"} />
          </Button>
          <RangePicker
            range={range}
            customFrom={customFrom}
            customTo={customTo}
            onRangeChange={(next) => { setRange(next); setPage(1) }}
            onCustomFromChange={(next) => { setCustomFrom(next); setPage(1) }}
            onCustomToChange={(next) => { setCustomTo(next); setPage(1) }}
          />
        </div>
      </div>

      <section className="panel-bg rounded-[22px] border border-border p-5" aria-label="Lista e filtros de conversas">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div><h3 className="font-display text-[15px] font-bold">Conversas</h3><p className="muted mt-0.5 text-[11px]">Pesquise por identificador ou texto e exporte os resultados filtrados.</p></div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="bg-background flex h-9 w-full items-center rounded-xl border border-border sm:w-[220px]"><Search className="text-muted-foreground ml-2.5 size-3.5" /><Input aria-label="Buscar conversas" className="h-full w-full border-0 bg-transparent px-2 text-xs shadow-none outline-none focus-visible:ring-0" placeholder="Buscar por ID ou texto..." value={search} onChange={(event) => { setSearch(event.target.value); setPage(1) }} /></label>
            <Select value={status} onValueChange={(next) => { setStatus(next); setPage(1) }}>
              <SelectTrigger className="h-9 w-[140px] rounded-xl text-xs" aria-label="Status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="active">Ativas</SelectItem>
                <SelectItem value="with-messages">Com mensagens</SelectItem>
                <SelectItem value="empty">Vazias</SelectItem>
                <SelectItem value="abandoned">Abandonadas</SelectItem>
                <SelectItem value="closed">Encerradas</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9 rounded-xl text-xs font-semibold" asChild><a href={api.exportConversationsUrl(chatbotId, { from, to, q: search || undefined, status, format: "csv" })} download><Download className="mr-1.5 size-3.5" />CSV</a></Button>
            <Button variant="outline" size="sm" className="h-9 rounded-xl text-xs font-semibold" asChild><a href={api.exportConversationsUrl(chatbotId, { from, to, q: search || undefined, status, format: "markdown" })} download><Download className="mr-1.5 size-3.5" />Markdown</a></Button>
          </div>
        </div>

        <div className="mt-4">
          {listQuery.isPending ? (
            <div className="space-y-2" aria-busy="true">{[1, 2, 3].map((index) => <Skeleton key={index} className="h-16 rounded-xl" />)}</div>
          ) : listQuery.isError ? (
            <Alert variant="destructive"><AlertTriangle className="size-4" /><AlertTitle>Não foi possível carregar as conversas</AlertTitle><AlertDescription className="flex items-center justify-between gap-3"><span>{listQuery.error instanceof Error ? listQuery.error.message : "Tente novamente."}</span><Button variant="outline" size="sm" onClick={() => void listQuery.refetch()}><RefreshCw className="size-3.5" />Tentar novamente</Button></AlertDescription></Alert>
          ) : !listQuery.data.items.length ? (
            <p className="muted py-8 text-center text-xs">{search || status !== "all" ? "Nenhuma conversa corresponde a estes filtros." : "Nenhuma conversa neste período."}</p>
          ) : (
            <>
              <ul className="divide-y divide-border">
                {listQuery.data.items.map((item) => (
                  <li key={item.id}>
                    <button type="button" onClick={() => setSelectedId(item.id)} className="flex w-full flex-col gap-1.5 px-2 py-3 text-left transition hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-row sm:items-center sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold">#{shortId(item.id)} <span className="muted font-normal">· {item.messageCount} mensagens</span></p>
                        <p className="muted truncate text-[11px]">{item.lastMessage ? item.lastMessage.content : "Sem mensagens ainda"}</p>
                      </div>
                      <span className="muted hidden text-[11px] md:block">{formatDateTime(item.startedAt)}</span>
                      <StatusBadge tone={statusTone(item.status)}>{statusLabel(item.status)}</StatusBadge>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-[11px] muted">
                <span>{listQuery.data.total} conversas · página {page} de {totalPages}</span>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" className="h-8 rounded-lg" aria-label="Página anterior" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}><ChevronLeft className="size-3.5" /></Button>
                  <Button variant="outline" size="sm" className="h-8 rounded-lg" aria-label="Próxima página" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}><ChevronRight className="size-3.5" /></Button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <Dialog open={Boolean(selectedId)} onOpenChange={(open) => { if (!open) setSelectedId(null) }}>
        <DialogContent className="bg-card max-h-[85vh] overflow-hidden rounded-2xl border-border p-0 sm:max-w-[520px]">
          <DialogHeader className="border-b border-border p-5">
            <DialogTitle className="font-display text-[17px]">Conversa #{selectedId ? shortId(selectedId) : ""}</DialogTitle>
          </DialogHeader>
          <ConversationDetailView chatbotId={chatbotId} conversationId={selectedId} onClose={() => setSelectedId(null)} />
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ConversationDetailView({ chatbotId, conversationId, onClose }: { chatbotId: string; conversationId: string | null; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ["chatbots", chatbotId, "conversations", conversationId ?? ""],
    queryFn: () => api.getConversation(chatbotId, conversationId!),
    enabled: Boolean(conversationId),
  })
  if (!conversationId) return null
  if (detailQuery.isPending) return <div className="space-y-2 p-5" aria-busy="true">{[1, 2, 3].map((index) => <Skeleton key={index} className="h-12 rounded-xl" />)}</div>
  if (detailQuery.isError || !detailQuery.data) return <div className="p-5"><Alert variant="destructive"><AlertTriangle className="size-4" /><AlertTitle>Não foi possível abrir a conversa</AlertTitle><AlertDescription>{detailQuery.error instanceof Error ? detailQuery.error.message : "Tente novamente."}</AlertDescription></Alert></div>
  const detail: ConversationDetail = detailQuery.data
  return (
    <div className="flex max-h-[60vh] flex-col">
      <div className="muted flex items-center gap-3 border-b border-border px-5 py-2.5 text-[10px]">
        <span className="flex items-center gap-1"><UserRound className="size-3" />Sessão {detail.sessionKey ? shortId(detail.sessionKey) : "—"}</span>
        <span>{formatDateTime(detail.startedAt)}</span>
      </div>
      <div className="chat-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
        {!detail.messages.length ? <p className="muted text-center text-xs">Nenhuma mensagem nesta conversa.</p> : detail.messages.map((message) => (
          message.role === "user" ? (
            <div key={message.id} className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2.5 text-[13px] leading-relaxed text-primary-foreground">
                {message.content}
                <span className="mt-1 block text-right text-[9px] opacity-70">{formatDateTime(message.createdAt)}</span>
              </div>
            </div>
          ) : (
            <div key={message.id} className="flex justify-start">
              <div className="bg-muted max-w-[80%] rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-[13px] leading-relaxed">
                {message.content}
                <span className="muted mt-1 block text-[9px]">{formatDateTime(message.createdAt)}</span>
              </div>
            </div>
          )
        ))}
      </div>
      <div className="border-t border-border p-4">
        <Button variant="outline" className="h-9 w-full rounded-xl text-xs font-semibold" onClick={onClose}><ArrowLeft className="mr-1.5 size-3.5" />Voltar ao histórico</Button>
      </div>
    </div>
  )
}

export function WorkspaceAnalyticsLink() {
  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2"><TrendingUp className="size-4 text-muted-foreground" /><h2 className="font-display text-[17px] font-bold">Insights por assistente</h2></div>
      <p className="muted mt-1 text-[12px]">Abra um assistente para ver tendências e <Link to="#assistants" className="font-semibold underline">consultar a lista</Link>.</p>
    </div>
  )
}
